import { AttachmentDisplay } from '@/components/shared/AttachmentDisplay';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { SxProps } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { uploadFileToUrl } from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/Tabs/UploadFileToUrl';
import { useAttachmentCaption } from '@/hooks/useAttachmentCaptions';

type Layout = 'card' | 'list';

type Props = {
  referenceId?: string;
  collectionName: string;
  selectedAttachment: Attachment;
  selectedAttachmentIndex: number;
  handleRemoveAttachment?: (index: number) => void;
  showRemoveIcon: boolean;
  enableUpload?: boolean;
  setIsUpdating?: (isUpdating: boolean) => void;
  onUploadComplete?: (index: number, file: Attachment) => void;
  isUploadDialog?: boolean;
  uploadAttachmentHandler?: () => Promise<void>;
  imageCaptureHandler?: (updatedCaption: string | undefined) => Promise<void>;
  handleClick?: (e: React.MouseEvent, attachment?: Attachment) => void;
  location?: string;
  handleSBSMode?: () => void;
  handleDownloadAttachment?: (index: number) => void;
  layout?: Layout;
  isRemovingDisabled?: boolean;
  sx?: SxProps;
};

export const FileDisplay = ({
  referenceId,
  collectionName,
  selectedAttachment,
  selectedAttachmentIndex,
  handleRemoveAttachment,
  showRemoveIcon,
  enableUpload = false,
  setIsUpdating,
  onUploadComplete,
  isUploadDialog,
  uploadAttachmentHandler,
  imageCaptureHandler,
  location = '',
  handleSBSMode,
  handleDownloadAttachment,
  layout = 'card',
  isRemovingDisabled = false,
  sx,
}: Props) => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const uploadInProgressRef = useRef<string | null>(null);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const {
    isEditing,
    setIsEditing,
    attachmentCaption,
    setAttachmentCaption,
    handleCaption,
    captionValidator,
  } = useAttachmentCaption();

  useEffect(() => {
    const uploadAttachments = async () => {
      const attachmentId = selectedAttachment.id || '';

      if (uploadInProgressRef.current === attachmentId) return;

      if (
        enableUpload &&
        selectedAttachment.isNew &&
        selectedAttachment.url &&
        selectedAttachment.file &&
        selectedAttachment.id
      ) {
        try {
          // Mark this attachment as being uploaded
          uploadInProgressRef.current = attachmentId;

          const uploadResult = await uploadFileToUrl(
            selectedAttachment,
            selectedAttachment.url,
            setUploadProgress
          );
          if (uploadResult && uploadAttachmentHandler) {
            // Make sure to pass the id to the handler even if it's undefined
            // The handler has its own check for undefined id
            await uploadAttachmentHandler();
            if (onUploadComplete) onUploadComplete(selectedAttachmentIndex, selectedAttachment);
            if (setIsUpdating) setIsUpdating(false);
          }
        } catch (error) {
          console.error('Upload failed:', error);
          uploadInProgressRef.current = null;
          setUploadProgress(0);
        }
      }
    };

    if (enableUpload) {
      uploadAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableUpload, selectedAttachment.id, selectedAttachmentIndex, referenceId, onUploadComplete]);

  const handleAttachmentCaption = async () => {
    setIsEditing(false);
    try {
      if (!captionValidator(selectedAttachment, referenceId)) return;
      if (imageCaptureHandler) {
        await imageCaptureHandler(attachmentCaption);
        showSnackBar('Attachment caption added successfully', 'success');
      }
    } catch (error) {
      showSnackBar('Failed to add Attachment caption', 'error');
    }
  };

  return (
    <AttachmentDisplay
      collectionName={collectionName}
      selectedAttachment={selectedAttachment}
      selectedAttachmentIndex={selectedAttachmentIndex}
      showRemoveIcon={showRemoveIcon}
      handleRemoveAttachment={handleRemoveAttachment}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      attachmentCaption={attachmentCaption}
      setAttachmentCaption={setAttachmentCaption}
      handleAttachmentCaption={handleAttachmentCaption}
      handleCaption={handleCaption}
      uploadProgress={uploadProgress}
      enableUpload={enableUpload}
      isUploadDialog={isUploadDialog}
      location={location}
      handleSBSMode={handleSBSMode}
      handleDownloadAttachment={handleDownloadAttachment}
      layout={layout}
      sx={sx}
      isRemovingDisabled={isRemovingDisabled}
    />
  );
};
